import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { api, downloadBinary, formatApiError } from "../lib/api";
import { loadGuestBiodata, clearGuestBiodata } from "../lib/biodata";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [hasGuestDraft, setHasGuestDraft] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || typeof user !== "object") {
      navigate("/login", { replace: true, state: { from: "/dashboard" } });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || typeof user !== "object") return;
    (async () => {
      try {
        const { data } = await api.get("/biodata");
        setItems(data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
    setHasGuestDraft(!!loadGuestBiodata());
  }, [user]);

  const importDraft = async () => {
    const draft = loadGuestBiodata();
    if (!draft) return;
    try {
      const {
        user_id: _u,
        id: _i,
        created_at: _c,
        updated_at: _up,
        share_slug: _s,
        is_primary: _p,
        ...safe
      } = draft;
      const { data } = await api.put("/profile/me", safe);
      toast.success("Draft merged into your matrimonial profile");
      clearGuestBiodata();
      setHasGuestDraft(false);
      navigate(`/biodata/${data.id}/edit`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this biodata?")) return;
    try {
      await api.delete(`/biodata/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !user || typeof user !== "object") {
    return (
      <div>
        <Navbar />
        <div className="container py-5">
          <div className="sb-card" data-testid="dashboard-loading">
            Loading your dashboard…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="container py-5" data-testid="dashboard-root">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
          <div>
            <div className="sb-kicker mb-1">Dashboard</div>
            <h1 className="h2 mb-1">Namaste, {user.name}.</h1>
            <p className="text-muted mb-0">Manage your biodatas and matrimonial profile.</p>
          </div>
          <Link to="/biodata/build" className="btn btn-primary" data-testid="dash-new-biodata">
            + New biodata
          </Link>
        </div>

        {hasGuestDraft && (
          <div
            className="sb-card mb-4 d-flex flex-wrap justify-content-between align-items-center gap-3"
            style={{ borderColor: "var(--sb-gold)" }}
            data-testid="guest-draft-banner"
          >
            <div>
              <div className="sb-kicker mb-1">Unsaved draft</div>
              <div>You have a biodata you built as a guest. Save it to your account?</div>
            </div>
            <button className="btn btn-gold" onClick={importDraft} data-testid="import-draft">
              Save draft
            </button>
          </div>
        )}

        <div className="row g-4 mb-4">
          <StatCard label="Biodatas" value={items?.length ?? "…"} testid="stat-biodatas" />
          <StatCard
            label="Membership"
            value={user.membership === "premium" ? "Premium" : "Free"}
            testid="stat-membership"
            highlight={user.membership === "premium"}
          />
          <StatCard
            label="Avg. completion"
            value={
              items && items.length
                ? `${Math.round(items.reduce((s, i) => s + i.completion, 0) / items.length)}%`
                : "—"
            }
            testid="stat-completion"
          />
        </div>

        <div className="row g-3 mb-4">
          <QuickTile
            testid="tile-profile"
            to="/profile/edit"
            kicker="Matrimonial profile"
            title="Edit my profile"
            body="Add photos, an about-me and go live for matches."
          />
          <QuickTile
            testid="tile-search"
            to="/search"
            kicker="Find matches"
            title="Search members"
            body="Filter by age, religion, city, education and more."
          />
          {user.membership !== "premium" && (
            <QuickTile
              testid="tile-upgrade"
              to="/premium"
              kicker="Membership"
              title="Upgrade to Premium"
              body="Reveal contacts, get priority listing and advanced filters."
              gold
            />
          )}
          {user.role === "admin" && (
            <QuickTile
              testid="tile-admin"
              to="/admin/payments"
              kicker="Admin"
              title="Payments queue"
              body="Review UPI payment requests and activate premium members."
            />
          )}
        </div>

        <h2 className="h4 mb-3">Your biodatas</h2>

        {error && <div className="alert alert-danger">{error}</div>}

        {items === null ? (
          <div className="sb-card">Loading…</div>
        ) : items.length === 0 ? (
          <div className="sb-card text-center py-5" data-testid="empty-biodatas">
            <div className="sb-kicker mb-2">Nothing here yet</div>
            <h3 className="h5">Create your first biodata</h3>
            <p className="text-muted mb-3">A beautiful biodata takes about 5 minutes.</p>
            <Link to="/biodata/build" className="btn btn-primary">
              Start building
            </Link>
          </div>
        ) : (
          <div className="row g-3">
            {items.map((b) => (
              <div className="col-md-6 col-lg-4" key={b.id}>
                <div className="sb-card h-100" data-testid={`biodata-card-${b.id}`}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h3 className="h5 mb-1">{b.title}</h3>
                      <div className="small text-muted">
                        {b.template} · {b.privacy}
                      </div>
                    </div>
                    <span className="sb-badge-soft">{b.completion}%</span>
                  </div>
                  <div className="sb-divider" style={{ margin: "1rem 0" }} />
                  <div className="d-flex gap-2 flex-wrap">
                    <Link
                      to={`/biodata/${b.id}/edit`}
                      className="btn btn-outline-primary btn-sm"
                      data-testid={`edit-${b.id}`}
                    >
                      Edit
                    </Link>
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/biodata/${b.id}`);
                          await downloadBinary("/export/pdf", data, "biodata.pdf");
                          toast.success("PDF downloaded");
                        } catch (e) {
                          toast.error(formatApiError(e));
                        }
                      }}
                      className="btn btn-outline-primary btn-sm"
                      data-testid={`download-pdf-${b.id}`}
                    >
                      ↓ PDF
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/biodata/${b.id}`);
                          await downloadBinary("/export/docx", data, "biodata.docx");
                          toast.success("DOCX downloaded");
                        } catch (e) {
                          toast.error(formatApiError(e));
                        }
                      }}
                      className="btn btn-outline-primary btn-sm"
                      data-testid={`download-docx-${b.id}`}
                    >
                      ↓ DOCX
                    </button>
                    <button
                      onClick={() => deleteItem(b.id)}
                      className="btn btn-outline-danger btn-sm rounded-pill"
                      data-testid={`delete-${b.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testid, highlight }) {
  return (
    <div className="col-6 col-md-4">
      <div className={"sb-card h-100" + (highlight ? " " : "")} data-testid={testid}>
        <div className="sb-kicker">{label}</div>
        <div className="mt-2" style={{ fontSize: "1.8rem", fontFamily: "'Cormorant Garamond', serif", color: "var(--sb-heading)" }}>
          {value}
        </div>
      </div>
    </div>
  );
}

function QuickTile({ testid, to, kicker, title, body, gold }) {
  return (
    <div className="col-md-6 col-lg-4">
      <Link
        to={to}
        className="sb-card h-100 text-decoration-none d-block"
        style={
          gold
            ? {
                borderColor: "var(--sb-gold)",
                background: "linear-gradient(135deg, rgba(212,175,55,0.10), rgba(212,175,55,0.04))",
              }
            : undefined
        }
        data-testid={testid}
      >
        <div className="sb-kicker mb-1">{kicker}</div>
        <h3 className="h5 mb-1" style={{ color: "var(--sb-heading)" }}>{title}</h3>
        <p className="mb-0 text-muted small">{body}</p>
      </Link>
    </div>
  );
}
